import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ScreenShareClient from '@/components/ScreenShareClient';
import { Monitor } from 'lucide-react';

/**
 * Public landing page for a screen-share invite link of the form
 * `/share/:code`. An admin generates the link from the Remote Control panel
 * and sends it to whoever needs help.
 *
 * No authentication required. The 6-character session code is itself the
 * authorization token — the backend `/screen-share/join/{code}` endpoint
 * confirms the session is open before any signaling happens, and the
 * signaling WebSocket is scoped by session_id. Letting anyone with the link
 * share is the whole point: the recipient is often a non-technical user
 * who shouldn't have to log in just to ask for help.
 */
const ShareScreenPage = () => {
  const { code: rawCode } = useParams();
  const navigate = useNavigate();
  const code = (rawCode || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#0F172A' }}>
      <div className="w-full max-w-md p-6 rounded-2xl space-y-4" style={{ background: '#1A2236', border: '2px solid #D4A853' }}>
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl" style={{ background: 'rgba(99,102,241,0.18)' }}>
            <Monitor size={22} style={{ color: '#818CF8' }} />
          </div>
          <div>
            <h1 className="text-lg font-black uppercase" style={{ color: '#F8F5EE' }}>
              Share your screen
            </h1>
            <p className="text-xs" style={{ color: '#94A3B8' }}>
              Semantic Vision support session
            </p>
          </div>
        </div>

        <div className="px-3 py-2 rounded-lg" style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.25)' }}>
          <p className="text-[10px] font-bold uppercase mb-1" style={{ color: '#94A3B8' }}>Session Code</p>
          <p className="text-2xl font-black tracking-[0.3em] text-center" style={{ color: '#F8F5EE' }} data-testid="share-code-display">
            {code || '------'}
          </p>
        </div>

        {!code || code.length < 4 ? (
          <p className="text-sm text-center" style={{ color: '#F87171' }}>
            That link doesn&apos;t look right. Ask the person who sent it for a fresh link.
          </p>
        ) : (
          <>
            <p className="text-xs" style={{ color: '#94A3B8' }}>
              No sign-in needed. Your browser will ask you to choose a tab or window to share — pick the page that needs help.
            </p>
            <ScreenShareClient
              initialCode={code}
              autoJoin
              onClose={() => navigate(-1)}
            />
          </>
        )}

        <p className="text-[10px] text-center" style={{ color: '#64748B' }}>
          Your screen is only shared with the support agent who sent you this link, and only while this tab is open.
        </p>
      </div>
    </div>
  );
};

export default ShareScreenPage;
